import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowRight, Check, FileText, Sparkles, Target, MessageSquare, Mail, Briefcase, UserCheck, Zap, ShieldCheck } from "lucide-react";

const contractTags = ["Alternance", "CDI", "CDD", "Stage", "Premier emploi"];

const features = [
  { icon: FileText, title: "Analyse de CV", desc: "Comprend en profondeur ton CV et détecte les compétences valorisables." },
  { icon: Target, title: "Analyse de l'offre", desc: "Extrait automatiquement les mots-clés et attentes du recruteur." },
  { icon: Sparkles, title: "Score de compatibilité", desc: "Un pourcentage indicatif clair pour prioriser tes candidatures." },
  { icon: UserCheck, title: "CV sur-mesure", desc: "Ton CV réorganisé pour matcher l'offre. Sans jamais inventer d'expérience." },
  { icon: Briefcase, title: "Lettre de motivation", desc: "Personnalisée pour le poste et l'entreprise, en un clic." },
  { icon: MessageSquare, title: "Message LinkedIn", desc: "Un message court, direct et pro pour aborder le recruteur." },
  { icon: Mail, title: "Email recruteur", desc: "Objet + corps prêts à envoyer, ton professionnel." },
  { icon: Zap, title: "Préparation entretien", desc: "5 questions probables, conseils et points à mettre en avant." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.08),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Badge className="rounded-full bg-blue-50 hover:bg-blue-50 text-blue-700 border-blue-100 font-medium px-3 py-1" data-testid="hero-badge">
                Offre de lancement · -40%
              </Badge>
              <h1 className="mt-6 font-jakarta text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-slate-900">
                Ton assistant IA pour<br />
                décrocher ton <span className="text-blue-600">prochain emploi</span>.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Ton CV ne devrait pas être le même pour chaque offre. Adapte-le, génère
                une candidature personnalisée et prépare ton entretien en quelques minutes.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {contractTags.map((t) => (
                  <span key={t} className="text-xs font-medium uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full px-3 py-1.5 bg-white">
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 h-12 shadow-md shadow-blue-600/20">
                  <Link to="/register" data-testid="hero-cta-primary">
                    Analyser mon offre <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12 border-slate-300">
                  <Link to="/#features" data-testid="hero-cta-secondary">Voir les fonctionnalités</Link>
                </Button>
              </div>

              <div className="mt-6 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />Aucune donnée inventée</div>
                <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-blue-600" />Résultat en 30 sec</div>
              </div>
            </div>

            {/* Preview Card */}
            <div className="lg:col-span-5">
              <Card className="p-6 sm:p-8 rounded-2xl border-slate-200 shadow-xl shadow-slate-200/50 bg-white">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">Aperçu</div>
                    <div className="mt-1 font-jakarta text-lg font-semibold text-slate-900">Compatibilité estimée</div>
                    <div className="text-xs text-slate-500 mt-0.5">Poste : Conseiller de vente · Alternance</div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">Analysé</Badge>
                </div>

                <div className="mt-6 flex items-center gap-6">
                  <div className="relative h-32 w-32">
                    <svg className="h-full w-full -rotate-90">
                      <circle cx="64" cy="64" r="52" stroke="#E2E8F0" strokeWidth="10" fill="none" />
                      <circle cx="64" cy="64" r="52" stroke="#2563EB" strokeWidth="10" strokeLinecap="round" fill="none" strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (1 - 0.78)} />
                    </svg>
                    <div className="absolute inset-0 grid place-items-center">
                      <span className="font-jakarta text-3xl font-extrabold text-slate-900">78%</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    {["Relation client · fort match", "Sens de l'organisation", "BTS MCO pertinent", "Ajouter chiffres résultats"].map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <span className={`h-1.5 w-1.5 rounded-full ${i < 3 ? "bg-emerald-500" : "bg-amber-500"}`} />
                        {s}
                      </div>
                    ))}
                  </div>
                </div>

                <p className="mt-6 text-xs text-slate-400 leading-relaxed">
                  Score indicatif basé uniquement sur les informations fournies. Il ne
                  garantit pas l'obtention d'un entretien ou d'un emploi.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 bg-slate-50/60 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Comment ça marche</div>
            <h2 className="mt-3 font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Trois étapes pour une candidature vraiment adaptée.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Ajoute ton CV", d: "Importe ton CV en PDF ou colle simplement son contenu." },
              { n: "02", t: "Colle l'offre d'emploi", d: "Copie l'offre depuis n'importe quel site. C'est tout." },
              { n: "03", t: "Obtiens une candidature adaptée", d: "Score, recommandations, CV, lettre, message, email, entretien." },
            ].map((s) => (
              <Card key={s.n} className="p-8 rounded-2xl border-slate-200 hover:shadow-md transition-shadow">
                <div className="font-jakarta text-5xl font-extrabold text-slate-200">{s.n}</div>
                <div className="mt-4 font-jakarta text-xl font-semibold text-slate-900">{s.t}</div>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Fonctionnalités</div>
            <h2 className="mt-3 font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Tout ce qu'il te faut, dans une seule interface.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <Card key={f.title} className="p-6 rounded-2xl border-slate-200 hover:border-blue-200 hover:shadow-md transition-all group">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-jakarta text-base font-semibold text-slate-900">{f.title}</div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 md:py-28 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Tarifs</div>
            <h2 className="mt-3 font-jakarta text-3xl sm:text-4xl font-bold tracking-tight">
              Une offre simple. Prix de lancement <span className="text-blue-400">-40%</span>.
            </h2>
            <p className="mt-4 text-slate-400 text-lg">
              Débloque toutes les fonctionnalités pendant la période de lancement.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <Card className="p-8 rounded-2xl bg-slate-900 border-slate-800 text-white">
              <div className="flex items-center justify-between">
                <div className="font-jakarta text-lg font-semibold">Découverte</div>
                <Badge className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-800">Gratuit</Badge>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-jakarta text-5xl font-extrabold">0€</div>
                <div className="text-slate-500">/ à vie</div>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {["Créer un compte", "Aperçu de l'analyse", "Score de compatibilité indicatif"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" />{f}</li>
                ))}
              </ul>
              <Button asChild variant="outline" className="mt-8 w-full rounded-full border-slate-700 bg-transparent text-white hover:bg-slate-800">
                <Link to="/register" data-testid="pricing-free">Commencer gratuitement</Link>
              </Button>
            </Card>

            <Card className="p-8 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500 text-white relative overflow-hidden">
              <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
              <div className="flex items-center justify-between relative">
                <div className="font-jakarta text-lg font-semibold">Pro</div>
                <Badge className="bg-white text-blue-700 hover:bg-white font-bold" data-testid="pricing-badge-pro">
                  -40% Lancement
                </Badge>
              </div>
              <div className="mt-4 flex items-baseline gap-3 relative">
                <div className="font-jakarta text-5xl font-extrabold" data-testid="pricing-launch">5,39€</div>
                <div className="text-blue-100 line-through" data-testid="pricing-regular">8,99€</div>
              </div>
              <div className="text-sm text-blue-100 mt-1">Paiement unique · Accès complet</div>
              <ul className="mt-6 space-y-3 text-sm text-white/90 relative">
                {[
                  "Analyse complète illimitée",
                  "Score de compatibilité détaillé",
                  "Recommandations actionnables",
                  "CV adapté à chaque offre",
                  "Lettre de motivation personnalisée",
                  "Message LinkedIn + Email recruteur",
                  "Préparation à l'entretien",
                  "Export des résultats",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-white" />{f}</li>
                ))}
              </ul>
              <Button asChild className="mt-8 w-full rounded-full bg-white text-blue-700 hover:bg-blue-50 font-semibold">
                <Link to="/register" data-testid="pricing-cta-pro">Passer Pro à 5,39€</Link>
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA + FOOTER */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Prêt à décrocher ton prochain emploi ?
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Un CV identique pour toutes les offres réduit la pertinence de ta candidature. AlternAI t'aide à personnaliser chaque candidature en quelques minutes.
          </p>
          <Button asChild size="lg" className="mt-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 h-12 shadow-md shadow-blue-600/20">
            <Link to="/register" data-testid="cta-footer">
              Créer ma candidature <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-md bg-blue-600 text-white">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="font-semibold text-slate-700">AlternAI</span>
            <span>· © 2026</span>
          </div>
          <div>Score indicatif · Ne garantit pas l'obtention d'un emploi.</div>
        </div>
      </footer>
    </div>
  );
}
